import { apiGet, apiPatch, apiPost, apiDelete, apiPut } from './http';

/* Camada da consola de SUPORTE & OPERAÇÃO — fala com as rotas /admin/* e
   /access/* (ver api/src/interfaces/http/routes/admin-console.js e access.js). */

// ── Ficha 360º de um utilizador ──
export interface OverviewUser {
  id: string; full_name: string | null; username: string | null; email: string | null;
  phone: string | null; user_role: string | null; tier: string | null; sector: string | null;
  company: string | null; job_title: string | null; location: string | null;
  avatar_url: string | null; wallet_balance: number | string | null; xp_total: number | null;
  is_active: boolean; onboarded_at: string | null; created_at: string; last_active_at: string | null;
}
export interface OverviewTicket {
  id: string; status: string; ticket_code: string | null; amount_paid: number | string | null;
  currency: string | null; created_at: string; event_title: string; event_slug: string; starts_at: string | null;
}
export interface OverviewTx { amount: number | string; tx_type: string; label: string | null; balance_after: number | string | null; created_at: string }
export interface OverviewChannels {
  telegram: { linked: boolean; username?: string | null; linked_at?: string | null };
  whatsapp: { number: string } | null;
}
export interface UserOverview {
  user: OverviewUser; tickets: OverviewTicket[]; wallet: OverviewTx[];
  connections: number | null; orgs: { id: string; name: string; role: string }[];
  channels?: OverviewChannels;   // opcional: ambiente sem a migração 078 não o traz
  degraded: string[];
}
export const getUserOverview = (id: string) => apiGet<UserOverview>(`/admin/users/${id}/overview`);

export function patchUser(id: string, values: { user_role?: string; tier?: string; is_active?: boolean }) {
  return apiPatch<{ ok: boolean }>(`/admin/users/${id}`, values);
}

// ── Impersonação auditada (motivo + TTL; trilho em /access/audit) ──
export const MIN_REASON_LENGTH = 10;
export interface GrantResponse { grantId: string; targetUserId: string; targetName?: string | null; expiresAt: string }
export function startImpersonation(input: { targetUserId: string; reason: string; ttlMinutes?: number }) {
  return apiPost<GrantResponse>('/access/impersonation', input);
}
export function endImpersonation(grantId: string) {
  return apiDelete<{ ok: boolean }>(`/access/impersonation/${grantId}`);
}

export interface AuditEntry {
  id?: string; action: string; actor_user_id?: string | null; target_user_id?: string | null;
  reason?: string | null; created_at?: string; metadata?: Record<string, unknown> | null;
}
export const getAudit = (params: { limit?: number } = {}) =>
  apiGet<{ entries: AuditEntry[] }>(`/access/audit?limit=${params.limit ?? 100}`);

// ── Organizações (tenants) ──
export interface AdminOrg {
  id: string; name: string; slug: string | null; plan_key: string | null;
  subscription_status: string | null; owner_id: string | null; owner_name: string | null;
  members: number; events: number; created_at: string;
}
export const listOrgs = () => apiGet<{ data: AdminOrg[]; error?: string }>('/admin/orgs');

// ── Marketplace (moderação) ──
export interface AdminProduct {
  id: string; name: string; description: string | null; price_kz: number | string | null;
  category: string | null; is_active: boolean; created_at: string;
}
export const listProducts = () => apiGet<{ data: AdminProduct[]; error?: string }>('/admin/marketplace');
export function setProductActive(id: string, is_active: boolean) {
  return apiPatch<{ ok: boolean }>(`/admin/marketplace/${id}/active`, { is_active });
}

// ── Fiscal (SempApp/AGT) ──
export interface FiscalCert {
  id: string; document_number: string; doc_type: string; provider: string; certified: boolean;
  software_label: string | null; fiscal_number: string | null; certification_code: string | null;
  verify_url: string | null; created_at: string;
}
export const getFiscal = () =>
  apiGet<{ data: FiscalCert[]; summary: { total: number; certified: number; failed: number } | null; error?: string }>('/admin/fiscal');

// ── BI (static embed assinado no servidor; light = fundo branco, melhor leitura) ──
export const getBiEmbed = (dashboard: number, theme: 'light' | 'night' = 'light') =>
  apiGet<{ url: string }>(`/admin/bi/embed?dashboard=${dashboard}&theme=${theme}`);

// ── CRUD: produtos (edição completa + remoção soft) ──
export interface ProductPatch {
  name?: string; category?: string; price_kz?: number; short_desc?: string;
  description?: string; image_url?: string; cta_label?: string; is_active?: boolean;
}
export const patchProduct = (id: string, values: ProductPatch) =>
  apiPatch<{ ok: boolean }>(`/admin/marketplace/${id}`, values);
export const deleteProduct = (id: string) =>
  apiDelete<{ ok: boolean }>(`/admin/marketplace/${id}`);

// ── CRUD: conteúdos (ler completo, editar, publicar/despublicar, apagar) ──
export interface FullContent {
  id: string; title: string | null; subtitle: string | null; summary: string | null;
  body_md: string | null; sector: string | null; kind: string | null; cover_url: string | null;
  media_url: string | null;
  is_free: boolean | null; is_featured: boolean | null; publish_status: string | null;
}
export const getContent = (id: string) => apiGet<{ content: FullContent }>(`/admin/content/${id}`);
export interface ContentPatch {
  title?: string; subtitle?: string; summary?: string; body_md?: string;
  sector?: string; kind?: string; cover_url?: string; media_url?: string; is_free?: boolean; is_featured?: boolean;
}
export const patchContent = (id: string, values: ContentPatch) =>
  apiPatch<{ ok: boolean }>(`/admin/content/${id}`, values);
export const setContentPublished = (id: string, published: boolean) =>
  apiPatch<{ ok: boolean }>(`/admin/content/${id}/publish`, { status: published ? 'published' : 'draft' });
export const deleteContent = (id: string) =>
  apiDelete<{ ok: boolean }>(`/admin/content/${id}`);

// ── CRUD: eventos (editar campos, cancelar) ──
export interface EventPatch {
  title?: string; description?: string; venue_name?: string; venue_city?: string;
  max_attendees?: number; sector?: string; starts_at?: string; ends_at?: string;
  status?: string; cover_url?: string;
}
export const putEvent = (id: string, values: EventPatch) =>
  apiPut<{ ok: boolean }>(`/admin/events/${id}`, values);
export const cancelEvent = (id: string) =>
  apiDelete<{ ok: boolean }>(`/admin/events/${id}`);

// ── Suporte (inbox Chatwoot via proxy da API — o token nunca chega aqui) ──
export interface SupportConversation {
  id: number; contact: string; email: string | null; channel: string | null;
  status: string; unread: number; last_message: string; last_activity_at: string | null;
}
export const listConversations = (status: 'open' | 'resolved' = 'open') =>
  apiGet<{ data: SupportConversation[]; meta: unknown }>(`/admin/support/conversations?status=${status}`);
export interface SupportMessage {
  id: number; content: string; direction: 'in' | 'out' | 'system'; author: string; created_at: string | null;
}
export const listMessages = (cid: number) =>
  apiGet<{ data: SupportMessage[] }>(`/admin/support/conversations/${cid}/messages`);
export const sendMessage = (cid: number, content: string) =>
  apiPost<{ ok: boolean }>(`/admin/support/conversations/${cid}/messages`, { content });
export const toggleConversation = (cid: number, status: 'open' | 'resolved') =>
  apiPost<{ ok: boolean }>(`/admin/support/conversations/${cid}/toggle`, { status });

// ── Hexis · Próximas ações (sinais operacionais reais do Overview) ──
export interface HexisAction { kind: string; title: string; cta: string; to: string }
export interface HexisActions { data: HexisAction[]; degraded: string[]; generated_at: string }
export const getHexisActions = () => apiGet<HexisActions>('/admin/hexis/actions');

// ── Perspetiva ADMIN do grafo de networking (mesma agregação do organizador) ──
export interface EventNetworkingTotals {
  taps: number; connections: number; active_participants: number;
  total_participants: number; isolated: number; avg_match: number | null; networking_rate: number;
}
export interface AdminEventNetworking {
  event_id: string; title: string;
  totals: EventNetworkingTotals;
  timeline: { t: string; value: number }[];
  by_sector: { sector: string; taps: number }[];
  top_people: { name: string; company: string | null; connections: number }[];
  top_companies: { company: string; connections: number }[];
  heatos: { zones: { name: string; density: number; level: string }[]; peak_hour: string | null };
}
export const getAdminEventNetworking = (id: string) =>
  apiGet<AdminEventNetworking>(`/admin/events/${id}/networking`);

// ── Atividade recente (timeline do ecossistema) ──
export interface ActivityItem { kind: string; dot: 'ok' | 'accent' | 'warn'; at: string; text: string }
export const getActivity = () => apiGet<{ data: ActivityItem[]; degraded: string[] }>('/admin/activity');
